import { call, put, select, takeEvery } from 'redux-saga/effects';
import {
  isCompanyContribution,
  isProductContribution,
  ResponseCreateContribution,
} from '@upright/common/model/input/contributions/types';
import { User } from '../../../../../common/iam/users/types';
import {
  sendAnonymousContribution as sendAnonymousContribution1,
  sendContribution as sendContribution1,
} from '../../../../api/contributions';
import { handleSagaException } from '../../../../common/generic/utilities/utils';
import { StateTree } from '../../reducers/types';
import { requestStarted, requestSucceeded, storePublicUsers } from '../../actions/actions';
import { updateModal } from '../modal';
import {
  appendEntityContributions,
  appendLatestContributions,
  SendContributionAction,
  storeContributions,
} from './actions';
import { EntityWithContribution } from './types';

function* sendContribution(action: SendContributionAction) {
  const { requestId, contribution } = action.payload;
  yield put(requestStarted(requestId));
  try {
    // PLT-4720: the JWT is HttpOnly now and unreadable from JS. Use the
    // Redux user state as the "is signed in" signal — equivalent to the
    // prior token check, since the user state is hydrated from
    // `/api/own-info` on app boot and cleared on logout.
    const loggedInUser = (yield select((s: StateTree) => s.user.user)) as User | undefined;
    if (!loggedInUser) {
      // Anonymous contribution
      yield call(sendAnonymousContribution1, contribution);
    } else {
      // The authenticated endpoint's second arg is unused (`_token`) since
      // the move to cookie-based auth; pass an empty string to keep the
      // call signature stable for one release.
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const { user, contribution: newContribution }: ResponseCreateContribution = yield call(
        sendContribution1,
        contribution,
        '',
      );
      yield put(storeContributions([newContribution]));
      yield put(storePublicUsers([user]));
      yield put(appendLatestContributions([newContribution.id]));
      yield put(appendEntityContributions(EntityWithContribution.USER, user.id.toString(), [newContribution.id]));
      if (isProductContribution(newContribution.type)) {
        yield put(
          appendEntityContributions(EntityWithContribution.PRODUCT, newContribution.targetId!, [newContribution.id]),
        );
      } else if (isCompanyContribution(newContribution.type)) {
        yield put(
          appendEntityContributions(EntityWithContribution.COMPANY, newContribution.targetId!, [newContribution.id]),
        );
      }
    }
    yield put(updateModal({ contributionSubmitComplete: true }));
    yield put(requestSucceeded(requestId));
  } catch (e) {
    yield handleSagaException(e, requestId);
  }
}

export default function* contributionDataRootSaga() {
  yield takeEvery('CONTRIBUTION/SEND', sendContribution);
}
